import React from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ProgressRing from '../components/ui/ProgressRing';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatNumber, formatTime } from '../utils/formatters';

const { FiGrid, FiClock, FiPackage, FiPlay, FiX, FiCheck } = FiIcons;

const HERO_IMAGE = 'https://cdn.midjourney.com/9697fcc3-16b9-4193-a351-57ccb8eb4fc3/0_3.webp';
const durationOptions = [1, 5, 15, 30, 60, 240];

export default function GardenPlot() {
  const { state, actions } = useGame();

  const getPlotProgress = (plot) => {
    if (plot.status !== 'growing') return 0;
    const now = Date.now();
    return Math.min(((now - plot.startedAt) / (plot.endAt - plot.startedAt)) * 100, 100);
  };

  const getPlotTimeRemaining = (plot) => {
    if (plot.status !== 'growing') return 0;
    return Math.max(0, Math.floor((plot.endAt - Date.now()) / 1000));
  };

  const getPlotReward = (minutes) => Math.ceil(10 * minutes);
  const getPlotCost   = (minutes) => Math.floor(getPlotReward(minutes) * 0.5);

  return (
    <div className="space-y-6">
      <div className="relative h-48 lg:h-64 rounded-3xl overflow-hidden shadow-xl">
        <div className="absolute inset-0 bg-black/30 z-10" />
        <img src={HERO_IMAGE} alt="Garden Plots" className="w-full h-full object-cover" />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 lg:p-8">
          <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-white mb-2">Garden Plots</h1>
          <p className="text-white/90 font-dm-sans max-w-lg">
            Strategic planting yields predictable harvests. Invest seeds now for guaranteed returns later.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {state.plots.map((plot, index) => {
          const progress      = getPlotProgress(plot);
          const timeRemaining = getPlotTimeRemaining(plot);
          const reward        = plot.status === 'ready' ? getPlotReward(plot.minutes) : plot.reward;
          const cost          = plot.status === 'ready' ? getPlotCost(plot.minutes)   : plot.cost;
          const canStart      = plot.status === 'ready' && state.seeds >= cost;

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="p-6 h-full flex flex-col" hover={false}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sage/20 rounded-xl flex items-center justify-center text-sage">
                      <SafeIcon icon={FiGrid} className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-cormorant font-bold text-charcoal text-lg">Plot {index + 1}</h3>
                      <p className="text-xs text-charcoal/50 uppercase tracking-wider font-bold">
                        {plot.status === 'ready' ? 'Fallow' : plot.status === 'growing' ? 'Cultivating' : 'Ready to Harvest'}
                      </p>
                    </div>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${
                    plot.status === 'ready'   ? 'bg-gray-300' :
                    plot.status === 'growing' ? 'bg-goldenrod animate-pulse' : 'bg-green-500'
                  }`} />
                </div>

                <div className="flex-grow flex flex-col justify-center items-center mb-6 min-h-[120px]">
                  {plot.status === 'growing' ? (
                    <ProgressRing progress={progress} size={100} strokeWidth={8} color="#E2A93B">
                      <div className="text-center">
                        <div className="text-lg font-bold text-charcoal font-dm-sans">{formatTime(timeRemaining)}</div>
                      </div>
                    </ProgressRing>
                  ) : plot.status === 'ready' ? (
                    <div className="w-full">
                      <label className="block text-xs font-bold text-charcoal/50 uppercase mb-2">Growth Cycle</label>
                      <select
                        value={plot.minutes}
                        onChange={(e) => actions.setPlotMinutes(index, parseInt(e.target.value))}
                        className="w-full p-3 border border-sage/20 rounded-xl bg-gray-50 text-charcoal font-dm-sans focus:outline-none focus:ring-2 focus:ring-sage/50"
                      >
                        {durationOptions.map((m) => (
                          <option key={m} value={m}>{m} minute{m !== 1 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="text-center text-sage">
                      <SafeIcon icon={FiCheck} className="w-16 h-16 mx-auto mb-2" />
                      <div className="font-bold">Harvest Ready!</div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="text-center p-3 bg-sage/10 rounded-xl border border-sage/10">
                    <div className="text-xs text-charcoal/50 uppercase font-bold mb-1">Yield</div>
                    <div className="text-lg font-bold text-charcoal flex items-center justify-center gap-1">
                      <SafeIcon icon={FiPackage} className="w-3 h-3 text-sage" />
                      {formatNumber(reward)}
                    </div>
                  </div>
                  {plot.status === 'ready' ? (
                    <div className="text-center p-3 bg-gray-100 rounded-xl border border-gray-200">
                      <div className="text-xs text-charcoal/50 uppercase font-bold mb-1">Cost</div>
                      <div className="text-lg font-bold text-charcoal">{formatNumber(cost)}</div>
                    </div>
                  ) : (
                    <div className="text-center p-3 bg-skyblue/10 rounded-xl border border-skyblue/10">
                      <div className="text-xs text-charcoal/50 uppercase font-bold mb-1">Cycle</div>
                      <div className="text-lg font-bold text-charcoal">{plot.minutes}m</div>
                    </div>
                  )}
                </div>

                <div className="mt-auto">
                  {plot.status === 'ready' && (
                    <Button variant={canStart ? 'primary' : 'secondary'} disabled={!canStart} onClick={() => actions.startPlot(index)} className="w-full">
                      {canStart ? 'Plant Seeds' : 'Insufficient Seeds'}
                    </Button>
                  )}
                  {plot.status === 'growing' && (
                    <Button variant="danger" onClick={() => actions.abortPlot(index)} icon={FiX} className="w-full bg-white border border-crimson/20 text-crimson hover:bg-crimson/5">
                      Abort (Refund)
                    </Button>
                  )}
                  {plot.status === 'done' && (
                    <Button variant="success" onClick={() => actions.claimPlot(index)} icon={FiCheck} className="w-full shadow-lg shadow-sage/20 animate-pulse-soft">
                      Harvest Rewards
                    </Button>
                  )}
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}